import React, { useState, useEffect } from 'react';
import { Hotel } from 'lucide-react';
import { HotelSelector } from '../components/hotelVisualization/HotelSelector';
import { BuildingSelector } from '../components/hotelVisualization/BuildingSelector';
import { BuildingView } from '../components/hotelVisualization/BuildingView';
import { useHotelVisualizationStore } from '../stores/hotelsVisualization/store';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useLanguage } from '../contexts/LanguageContext';

export const HotelVisualizationPage = () => {
  const { hotels, loading, error, fetchHotels } = useHotelVisualizationStore();
  const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(
    null
  );
  const { translations } = useLanguage();

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  useEffect(() => {
    // Set initial selections when data is loaded
    if (!selectedHotelId && Object.keys(hotels).length > 0) {
      const firstHotelId = Object.keys(hotels)[0];
      setSelectedHotelId(firstHotelId);
      setSelectedBuildingId(hotels[firstHotelId].buildings[0].id);
    }
  }, [hotels, selectedHotelId]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  const selectedHotel = selectedHotelId ? hotels[selectedHotelId] : null;
  const selectedBuilding = selectedHotel?.buildings.find(
    (b) => b.id === selectedBuildingId
  );

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary-50 rounded-lg">
          <Hotel className="text-primary-500" size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {translations?.hotelVisualizationPage?.headerTitle ||
              'Hotel Visualization'}
          </h1>
          <p className="text-gray-500 mt-1">
            {translations?.hotelVisualizationPage?.headerSubtitle ||
              'View details of hotels, buildings, and rooms'}
          </p>
        </div>
      </div>

      {/* Hotel Selection */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm">
        <h2 className="text-lg font-medium mb-4">
          {translations?.hotelVisualizationPage?.hotelSelectionTitle ||
            'Select a Hotel'}
        </h2>
        <HotelSelector
          hotels={Object.values(hotels)}
          selectedHotelId={selectedHotelId || ''}
          onSelectHotel={(hotelId) => {
            setSelectedHotelId(hotelId);
            setSelectedBuildingId(hotels[hotelId].buildings[0].id);
          }}
        />
      </div>

      {/* Building Selection (if multiple buildings) */}
      {selectedHotel?.hasMultipleBuildings && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm">
          <h2 className="text-lg font-medium mb-4">
            {translations?.hotelVisualizationPage?.buildingSelectionTitle ||
              'Select a Building'}
          </h2>
          <BuildingSelector
            buildings={selectedHotel.buildings}
            selectedBuildingId={selectedBuildingId || ''}
            onSelectBuilding={setSelectedBuildingId}
          />
        </div>
      )}

      {/* Building View */}
      {selectedBuilding && <BuildingView building={selectedBuilding} />}
    </div>
  );
};